import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleListItem from "@/components/feature/ArticleListItem";
import MediaCard from "@/components/feature/MediaCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import {
  findArticleById,
  findMediaById,
  formatDate,
  pickRandomRelated,
} from "@/lib/content";
import { mediaList } from "@/mocks/media";
import { articleList } from "@/mocks/articles";

export default function DetailPage() {
  const { id = "" } = useParams();
  const media = findMediaById(id);
  const article = findArticleById(id);
  const { isFavorite, toggleFavorite, favoriteIds } = useFavorites();
  const [toast, setToast] = useState("");

  const title = media ? `${media.title} | 蜜桃传媒` : article ? `${article.title} | 蜜桃传媒` : "内容暂不可用 | 蜜桃传媒";
  const description = media?.summary || article?.summary || "该内容暂不可用，请返回浏览其他影视资讯。";
  usePageMeta(title, description);

  const related = useMemo(() => {
    if (media) {
      return pickRandomRelated(
        mediaList.filter((item) => item.type === media.type),
        media.id,
        4
      );
    }
    if (article) {
      return pickRandomRelated(
        articleList.filter((item) => item.section === article.section),
        article.id,
        4
      );
    }
    return [];
  }, [media, article]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，可分享给朋友");
    } catch {
      showToast("复制失败，请手动复制浏览器地址");
    }
  };

  const handleFavorite = () => {
    const added = toggleFavorite(id);
    showToast(added ? "已加入收藏" : "已取消收藏");
  };

  if (!media && !article) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6">
        <Breadcrumb items={[{ label: "内容详情" }]} />
        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-black text-foreground-950">内容暂不可用</h1>
          <p className="mt-2 text-sm text-foreground-600">
            该内容可能已被移除或链接有误，欢迎返回首页浏览其他影视资讯。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(id);
  const categoryKey = media ? media.type : article?.relatedCategory || "movie";
  const categoryLink =
    categoryKey === "star-news" ? "/star-news" : `/category/${categoryKey}`;
  const categoryName =
    categoryKey === "movie" ? "电影" : categoryKey === "tv" ? "电视剧" : "明星动态";

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: categoryName, to: categoryLink },
          { label: media?.title || article?.title || "" },
        ]}
      />

      <article className="mt-5">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-8">
          <div className="w-full max-w-[300px]">
            <div
              className="relative w-full overflow-hidden rounded-lg bg-background-100"
              style={{ aspectRatio: media ? "2 / 3" : "3 / 2" }}
            >
              <img
                src={media?.cover || article?.cover}
                alt={`${media?.title || article?.title} 封面`}
                title={`${media?.title || article?.title} 内容封面`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md py-2.5 text-sm font-bold transition-colors ${
                  favorited
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-share-line"></i>
                分享
              </button>
            </div>
            {favoriteIds.length > 0 && (
              <p className="mt-2 text-center text-[11px] text-foreground-500">
                收藏保存在本浏览器，共 {favoriteIds.length} 条
              </p>
            )}
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-2.5 py-1 text-xs font-bold text-primary-700">
                {media ? (media.type === "movie" ? "电影" : "电视剧") : "影视资讯"}
              </span>
              {media && (
                <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-800">
                  <i className="ri-star-fill"></i>
                  {media.rating.toFixed(1)}
                </span>
              )}
              {(media?.tags || article?.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
              {media?.title || article?.title}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {media?.summary || article?.summary}
            </p>

            {media && (
              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:grid-cols-2 md:p-5">
                <div className="flex gap-2 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-500">上映时间</dt>
                  <dd className="font-medium text-foreground-900">{media.releaseDate}</dd>
                </div>
                <div className="flex gap-2 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                  <dd className="font-medium text-foreground-900">{media.director}</dd>
                </div>
                <div className="flex gap-2 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                  <dd className="font-medium text-foreground-900">{media.cast.join("、")}</dd>
                </div>
                <div className="flex gap-2 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-500">类型</dt>
                  <dd className="font-medium text-foreground-900">{media.genres.join(" / ")}</dd>
                </div>
                <div className="flex gap-2 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-500">上映地区</dt>
                  <dd className="font-medium text-foreground-900">{media.region}</dd>
                </div>
                <div className="flex gap-2 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-500">片长</dt>
                  <dd className="font-medium text-foreground-900">{media.duration}</dd>
                </div>
              </dl>
            )}

            {article && (
              <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-100 p-4 text-sm md:p-5">
                <div className="flex gap-2">
                  <dt className="text-foreground-500">发布时间</dt>
                  <dd className="font-medium text-foreground-900">{formatDate(article.date)}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500">栏目</dt>
                  <dd className="font-medium text-foreground-900">
                    {article.section === "star" ? "明星动态" : "影视资讯"}
                  </dd>
                </div>
              </dl>
            )}

            <div className="mt-6">
              <h2 className="font-heading text-lg font-black text-foreground-950">
                {media ? "剧情简介" : "正文内容"}
              </h2>
              <p className="mt-3 whitespace-pre-line text-sm leading-loose text-foreground-700 md:text-base">
                {media?.content || article?.content}
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">相关分类：</span>
              <Link
                to={categoryLink}
                className="flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-price-tag-3-line"></i>
                {categoryName}
              </Link>
              <Link
                to="/star-news"
                className="flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-user-star-line"></i>
                明星动态
              </Link>
              <Link
                to="/special/sp-01"
                className="flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-bookmark-3-line"></i>
                精选专题
              </Link>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-12" aria-label="推荐内容">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-thumb-up-line text-xl"></i>
            </span>
            <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">
              为你推荐
            </h2>
          </div>

          {media ? (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {(related as typeof mediaList).map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {(related as typeof articleList).map((item) => (
                <ArticleListItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm font-medium text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}